import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEvaluationReviews } from '../../services/ExpertReviewService';

const statusStyle = {
  approved: 'bg-green-100 text-green-800',
  awaiting_review: 'bg-amber-100 text-amber-800',
  generating: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
};

export default function ExpertReviewQueue() {
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const reviewBasePath = location.pathname.startsWith('/organization/')
    ? '/organization/reviews'
    : '/expert/reviews';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getEvaluationReviews({ status: status || undefined, search: search || undefined, page })
      .then((data) => {
        if (!active) return;
        const rows = data?.results || (Array.isArray(data) ? data : []);
        setReviews(rows);
        setCount(data?.count ?? rows.length);
      })
      .catch(() => {
        if (active) setError('Unable to load the expert review queue.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [page, search, status]);

  const totalPages = Math.max(1, Math.ceil(count / 30));

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Interview evaluation reviews</h1>
          <p className="text-gray-600 dark:text-gray-300">
            AI drafts remain private until an assigned expert approves them.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <form
            role="search"
            className="flex"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setSearch(searchInput.trim());
            }}
          >
            <label htmlFor="review-search" className="sr-only">Search reviews</label>
            <input id="review-search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Student or interview" className="rounded-l border p-2 text-gray-900" />
            <button type="submit" className="rounded-r bg-blue-600 px-4 py-2 text-white">Search</button>
          </form>
          <label htmlFor="review-status" className="sr-only">Filter by review status</label>
          <select
            id="review-status"
            value={status}
            onChange={(event) => { setStatus(event.target.value); setPage(1); }}
            className="rounded border p-2 text-gray-900"
          >
            <option value="">All statuses</option>
            <option value="awaiting_review">Awaiting review</option>
            <option value="generating">Generating</option>
            <option value="approved">Approved</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>
      {loading && <p role="status" className="text-gray-500">Loading reviews…</p>}
      {error && <p role="alert" className="text-red-600">{error}</p>}
      <div className="grid gap-4">
        {reviews.map((review) => (
          <article
            key={review.uuid}
            className="text-left rounded-xl border bg-white p-5 shadow-sm hover:shadow-md dark:bg-gray-800 dark:border-gray-700"
          >
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <h2 className="font-semibold text-lg">
                  {review.student.first_name} {review.student.last_name}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {review.interview.name} · {review.student.email}
                </p>
              </div>
              <span
                className={`h-fit rounded-full px-3 py-1 text-sm ${
                  statusStyle[review.status] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {review.status.replaceAll('_', ' ')}
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => navigate(`${reviewBasePath}/${review.uuid}`)}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              >
                Open review
              </button>
              <button
                onClick={() => navigate(`/portfolio/${review.student.uuid}`)}
                className="rounded border border-blue-600 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-200"
              >
                View portfolio
              </button>
            </div>
          </article>
        ))}
        {!loading && !reviews.length && !error && (
          <p className="rounded border border-dashed p-8 text-center text-gray-500">
            No reviews match this filter.
          </p>
        )}
      </div>
      {!loading && !error && count > 0 && (
        <nav aria-label="Review queue pagination" className="flex items-center justify-between gap-3">
          <button disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-4 py-2 disabled:opacity-40">Previous</button>
          <span className="text-sm">Page {page} of {totalPages} · {count} reviews</span>
          <button disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-4 py-2 disabled:opacity-40">Next</button>
        </nav>
      )}
    </div>
  );
}
